/**
 * Diamond Node Component (New - BaseNode Implementation)
 * Diamond-shaped node for conditionals and decision points
 */

import React from 'react';
import { NodeProps } from 'reactflow';
import { WorkflowNode, getNodeDisplayProps } from '@flow-studio/types';
import { NodeLeftIcon } from '../NodeIcon';
import { BaseNodeClass, withNodeHooks, NodeConfig } from './Base/BaseNode';

const PinIcon = ({ size = 10, color = 'white' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);

/**
 * DiamondNode Class extending BaseNode
 */
class DiamondNodeClass extends BaseNodeClass {
  protected config(): NodeConfig {
    return {
      ...super.config(),
      showToolbarOnClick: true,
      enablePropertiesModal: true,
      enableRenameModal: true,
      enableMoreMenu: true,
    };
  }

  protected getClassName(): string {
    return 'diamond-node';
  }

  protected supportsConfigDimensions(): boolean { return false; }

  protected getContainerStyle(): React.CSSProperties {
    const isCollapsed = this.state.isCollapsed;
    const size = isCollapsed ? '70px' : '140px';
    const rawStyle = (this.props.data.processElement?.configuration?.designer?.ui?.style ?? {}) as React.CSSProperties;
    const { width: _w, height: _h, ...userStyle } = rawStyle as any;

    return {
      width: size,
      height: size,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...userStyle,
    };
  }

  protected renderContent(): React.ReactNode {
    const { selected, data } = this.props;
    const isCollapsed = this.state.isCollapsed;
    const display = getNodeDisplayProps(data);

    const icon = <NodeLeftIcon icon={display.icon} category={display.category} size={14} color="white" />;

    const isDisabled = data.processElement?.isDisabled;
    const isSelected = selected;

    const diamondSize = isCollapsed ? '50px' : '100px';

    const explicitColor = data.processElement?.configuration?.designer?.ui?.color;
    const diamondBg = explicitColor ?? 'rgba(2, 6, 23, 0.92)';

    const diamondStyle: React.CSSProperties = {
      width: diamondSize,
      height: diamondSize,
      background: diamondBg,
      border: `3px solid ${
        isSelected
          ? '#3A8C45'
          : isDisabled
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(58, 140, 69, 0.3)'
      }`,
      transform: 'rotate(45deg)',
      opacity: isDisabled ? 0.6 : 1,
      boxShadow: isSelected
        ? '0 0 0 2px #3A8C45, 0 0 30px rgba(58,140,69,0.6)'
        : '0 4px 20px rgba(0,0,0,0.6)',
      transition: 'all 0.2s',
      position: 'relative',
      cursor: 'move',
    };

    const contentStyle: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) rotate(-45deg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      zIndex: 1,
      gap: '4px',
    };

    return (
      <>
      <div style={diamondStyle}>
        <div style={contentStyle}>
          {/* Icon — now uses proper React component, not string */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </div>

          {/* Title — now uses getNodeDisplayProps */}
          <div className="bf-node-header-title" style={{
            fontSize: '10px',
            fontWeight: 700,
            color: '#f8fafc',
            textAlign: 'center',
            lineHeight: '1.2',
            maxWidth: '70px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            {display.displayName}
          </div>

          {/* Execution status — now reads from correct field */}
          {data.runtime?.executionStatus && data.runtime.executionStatus !== 'idle' && !['success', 'failed', 'skipped', 'timeout'].includes(data.runtime.executionStatus) && (
            <div style={{
              fontSize: '8px',
              padding: '2px 4px',
              borderRadius: '4px',
              background:
                data.runtime.executionStatus === 'success' ? 'rgba(16,185,129,0.2)'
                : data.runtime?.executionStatus === 'error' ? 'rgba(239,68,68,0.2)'
                : data.runtime.executionStatus === 'running' ? 'rgba(59,130,246,0.2)'
                : 'transparent',
              color:
                data.runtime?.executionStatus === 'success' ? '#34d399'
                : data.runtime?.executionStatus === 'error' ? '#fb7185'
                : data.runtime?.executionStatus === 'running' ? '#60a5fa'
                : 'transparent',
            }}>
              {data.runtime.executionStatus}
            </div>
          )}
        </div>
      </div>
      {data.processElement?.pinnedData && (
        <div title="Output pinned" style={{ position: 'absolute', top: 2, left: 2, opacity: 0.85 }}>
          <PinIcon size={10} color="white" />
        </div>
      )}
      </>
    );
  }

  protected onRun(): void {
    console.log('Run step:', this.props.id);
  }
}

/**
 * Export with hooks injected
 */
export const DiamondNode: React.FC<NodeProps<WorkflowNode['data']>> =
  withNodeHooks(DiamondNodeClass);
